// 約定結果タブ (AC-BSP-037) のフィルタ。
//
// フィルタは通常入札とブロック入札の両方に同じく効く。 ブロック入札はエリア・売買を Leg ごとに、
// コマを対象コマ範囲との重なりで判定する (KPI カード・エリア × 売買集計に選んだコマの分だけを
// 入れるのは `toBlockExecutionSummaryRecords`)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import type { BlockBidRow } from './blockBidRow'
import { deriveBlockBidStatusCategory } from './blockBidStatusCategory'
import type { SpotBidRow } from './spotBidRow'
import {
  type SpotBidStatusCategory,
  deriveSpotBidStatusCategory,
} from './spotBidStatusCategory'

/** 約定結果タブのフィルタ。 どの軸も空集合は「すべて」。 */
export type SpotExecutionResultFilter = {
  komas: ReadonlySet<number>
  areas: ReadonlySet<AreaCode>
  sides: ReadonlySet<Side>
  statuses: ReadonlySet<SpotBidStatusCategory>
}

const matchesAll = <T>(selected: ReadonlySet<T>, value: T): boolean =>
  selected.size === 0 || selected.has(value)

/** フィルタに合う通常入札の札。 */
export const filterNormalExecutionBids = (
  rows: readonly SpotBidRow[],
  filter: SpotExecutionResultFilter,
  isPastDeadline: boolean,
): SpotBidRow[] =>
  rows.filter(
    (row) =>
      !row.needsReview &&
      matchesAll(filter.komas, row.timeCode) &&
      matchesAll(filter.areas, row.areaCode) &&
      matchesAll(filter.sides, row.direction) &&
      matchesAll(
        filter.statuses,
        deriveSpotBidStatusCategory(row, isPastDeadline),
      ),
  )

/** フィルタに合うブロック入札の Leg。 コマは対象コマ範囲に選んだコマを含むかで見る。 */
export const filterBlockExecutionLegs = (
  rows: readonly BlockBidRow[],
  filter: SpotExecutionResultFilter,
  isPastDeadline: boolean,
): BlockBidRow[] =>
  rows.filter(
    (row) =>
      !row.needsReview &&
      (filter.komas.size === 0 ||
        row.slotCodes.some((code) => filter.komas.has(code))) &&
      matchesAll(filter.areas, row.areaCode) &&
      matchesAll(filter.sides, row.direction) &&
      matchesAll(
        filter.statuses,
        deriveBlockBidStatusCategory(row, isPastDeadline),
      ),
  )
